// [Codex 2026-07-28] Running status remains accessible without duplicating the
// composer's visible send/stop control.
import { Pause, Play, Volume2 } from "lucide-react";
import { DynamicIsland, DynamicIslandView } from "../../motion/dynamic-island";
import { FintheonNumber } from "../../numbers/FintheonNumber";
import type { GlobalComposerController } from "./composer-controller";

export function ChatActivityIsland({
  controller,
}: {
  controller: GlobalComposerController;
}) {
  if (controller.activity === "idle") return null;
  const label = controller.activityLabel ?? activityLabel(controller.activity);
  if (controller.activity === "running") {
    return (
      <span className="sr-only" role="status" aria-live="polite">
        {label}
      </span>
    );
  }

  return (
    <div className="pointer-events-auto absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
      <DynamicIsland
        view={controller.activity}
        className="bg-[var(--fintheon-text)] text-[var(--fintheon-bg)] shadow-none"
      >
        <DynamicIslandView
          id={controller.activity}
          className="min-h-8 gap-2 px-3 py-1.5 text-xs"
        >
          <ActivityIcon activity={controller.activity} />
          <span>{label}</span>
          {controller.activity === "queued" && controller.queueCount ? (
            <FintheonNumber
              value={controller.queueCount}
              kind="count"
              suffix=" queued"
            />
          ) : null}
          {controller.activity === "paused" && controller.onResume ? (
            <button
              type="button"
              onClick={controller.onResume}
              aria-label="Resume response"
              className="ml-1 inline-flex size-7 items-center justify-center bg-transparent"
            >
              <Play size={13} fill="currentColor" />
            </button>
          ) : null}
        </DynamicIslandView>
      </DynamicIsland>
    </div>
  );
}

function ActivityIcon({
  activity,
}: {
  activity: GlobalComposerController["activity"];
}) {
  if (activity === "voice") return <Volume2 size={13} />;
  if (activity === "paused") return <Pause size={13} />;
  return (
    <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
  );
}

function activityLabel(activity: GlobalComposerController["activity"]) {
  if (activity === "approval") return "Approval needed";
  if (activity === "voice") return "Listening";
  if (activity === "paused") return "Paused";
  if (activity === "queued") return "Queue";
  return "Working";
}
